import { useState, useEffect, useCallback } from "react";
import Button from "../../Components/Button";
import EventInformation from "./EventInformation";
import axios from "axios";
import {
  GET_EVENT_INFO_API,
  GET_FONTS_API,
  UPDATE_EVENT_INFO_API,
} from "../../Constant/api";
import { useToast } from "../../Components/Toast";
import LoadingInComponent from "../../Components/LoadingInComponent";
import { LoadingOverlay } from "../../Components/LoadingOverlay";
import Breadcrumb from "../../Components/Breadcrumb";
import Customizations from "./Customizations";
import MonetaryContribution from "./MonetaryContribution";
import { EVENT_PREVIEW } from "../../Router/path";
import { useNavigate } from "react-router-dom";
import { loadGoogleFont } from "../../Constant/fontLoader";
import Can from "../../Components/Can";

const EventPage = () => {
  const [activeTab, setActiveTab] = useState("Event Information");

  const [eventInfo, setEventInfo] = useState(null);
  const [templateCustomizations, setTemplateCustomizations] = useState(null);
  const [monetaryContributionSettings, setMonetaryContributionSettings] =
    useState(null);
  const [fontOptions, setFontOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);

  const { toast } = useToast();
  const navigate = useNavigate();

  const tabButtons = [
    "Event Information",
    "Customizations",
    "Monetary Contribution Settings",
  ];

  const breadcrumbItems = [
    { label: "Event Page" },
    { label: activeTab },
    { label: "Edit" },
  ];

  const fetchEventInfo = useCallback(() => {
    setLoading(true);
    axios
      .get(GET_EVENT_INFO_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
        params: { eventId: sessionStorage.getItem("event_id") },
      })
      .then((response) => {
        const data = response.data.data;
        const eventData = data.event_info;

        // Ensure floor_plan and image are arrays
        if (!Array.isArray(eventData.floor_plan)) {
          eventData.floor_plan = eventData.floor_plan ? [eventData.floor_plan] : [];
        }
        if (!Array.isArray(eventData.image)) {
          eventData.image = eventData.image ? [eventData.image] : [];
        }

        // Merge with any existing client-side edits to prevent flicker/reset
        setEventInfo((prev) => {
          if (!prev) return eventData;
          // Keep local temporary uploads (containing 'upload_temp') that aren't in server data yet
          const prevFloor = Array.isArray(prev.floor_plan) ? prev.floor_plan : [];
          const prevImage = Array.isArray(prev.image) ? prev.image : [];

          const localTempFloor = prevFloor.filter((url) =>
            url.includes("upload_temp"),
          );
          const localTempImage = prevImage.filter((url) =>
            url.includes("upload_temp"),
          );

          const serverFloor = Array.isArray(eventData.floor_plan)
            ? eventData.floor_plan
            : [];
          const serverImage = Array.isArray(eventData.image)
            ? eventData.image
            : [];

          // Merge server data with local temporary (unsaved) uploads
          // This prevents newly uploaded images from disappearing if a background fetch completes
          return {
            ...eventData,
            floor_plan: [...serverFloor, ...localTempFloor],
            image: [...serverImage, ...localTempImage],
          };
        });
        setTemplateCustomizations(data.customizations);
        setMonetaryContributionSettings(data.monetary);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.log("Failed to fetch event info", error);
        toast("Failed to fetch event info", "error");
      });
  }, [toast]);

  const fetchFonts = useCallback(async () => {
    await axios
      .get(GET_FONTS_API, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
        },
      })
      .then((res) => {
        const fonts = res.data.data.map((font) => ({
          id: font.id,
          name: font.family,
          family: font.family,
          label: font.family,
          value: font.id,
          weights: font.weights,
          styles: font.styles,
          subsets: font.subsets,
        }));

        setFontOptions(fonts);

        fonts.forEach((font) => {
          if (
            font.id === templateCustomizations.title_font ||
            font.id === templateCustomizations.content_font
          ) {
            loadGoogleFont(font.family);
          }
        });
      })
      .catch((error) => {
        console.log("Failed to fetch fonts", error);
        toast("Failed to fetch fonts", "error");
      });
  }, [templateCustomizations, toast]);

  const handleUpdate = () => {
    if (eventInfo.name !== "") {
      setSubmitLoading(true);

      const payload = {
        // Event table fields
        name: eventInfo.name,
        venue: eventInfo.venue,
        date: eventInfo.date,
        time: eventInfo.time,
        seat_management: eventInfo.seat_management,
        dietary_management: eventInfo.dietary_management,
        template_id: eventInfo.template_id,
        floor_plan: Array.isArray(eventInfo.floor_plan)
          ? eventInfo.floor_plan
          : eventInfo.floor_plan
            ? [eventInfo.floor_plan]
            : [],
        image: Array.isArray(eventInfo.image)
          ? eventInfo.image
          : eventInfo.image
            ? [eventInfo.image]
            : [],
        description: eventInfo.description,

        // EventCustomization fields
        title_font: templateCustomizations.title_font,
        content_font: templateCustomizations.content_font,
        theme_color: templateCustomizations.theme_color,
        image_customization: templateCustomizations.image,

        // MonetaryContribution fields
        monetary_enabled: monetaryContributionSettings.accept_contribution,
        monetary_title: monetaryContributionSettings.monetary_title,
        monetary_description: monetaryContributionSettings.monetary_description,
        receive_options: monetaryContributionSettings.receive_options,
        amount_options: Array.isArray(
          monetaryContributionSettings.amount_options,
        )
          ? monetaryContributionSettings.amount_options
          : monetaryContributionSettings.amount_options
            ? [monetaryContributionSettings.amount_options]
            : [],
        qr_code: monetaryContributionSettings.qr_code,
      };

      axios
        .put(UPDATE_EVENT_INFO_API, payload, {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem("bearer_token")}`,
          },
          params: { eventId: sessionStorage.getItem("event_id") },
        })
        .then((response) => {
          sessionStorage.setItem("seat", eventInfo.seat_management);
          sessionStorage.setItem("dietary", eventInfo.dietary_management);
          toast("Event info updated successfully", "success");
          setTimeout(() => {
            setSubmitLoading(false);
            window.location.reload();
            fetchEventInfo();
          }, 2000);
        })
        .catch((error) => {
          setSubmitLoading(false);
          console.log("Failed to update event info", error.response || error);
          toast("Failed to update event info", "error");
        });
    } else {
      toast("Please fill in all required fields", "error");
    }
  };

  // const handlePageClick = (e) => {
  //   fetchTemplateList(e.selected + 1);
  // };

  const handlePreview = () => {
    navigate(EVENT_PREVIEW, {
      state: {
        eventInfo,
        customizations: templateCustomizations,
        monetary: monetaryContributionSettings,
      },
    });
  };

  useEffect(() => {
    fetchEventInfo();
  }, [fetchEventInfo]);

  useEffect(() => {
    if (templateCustomizations) {
      fetchFonts();
    }
  }, [templateCustomizations, fetchFonts]);

  return (
    <div className="flex flex-col w-full h-full">
      {submitLoading && <LoadingOverlay />}
      <LoadingInComponent isLoading={loading}>
        <div className="flex flex-col w-full h-full p-[16px] md:p-[32px] no-scrollbar overflow-y-auto gap-[24px]">
          {/* Breadcrumb */}
          <Breadcrumb items={breadcrumbItems} />

          {/* Title and buttons */}
          <div className="flex flex-col md:flex-row w-full justify-between items-center gap-4">
            <h1 className="text-[29px]">{activeTab}</h1>
            <div className="flex w-fit items-center gap-[8px]">
              <Can permission="edit_event_page">
                <Button
                  variant="secondary"
                  onClick={handlePreview}
                  disabled={loading}
                >
                  Preview
                </Button>
                <Button
                  variant="primary"
                  onClick={handleUpdate}
                  disabled={loading}
                >
                  Save Changes
                </Button>
              </Can>
            </div>
          </div>

          {/* Event information section */}
          <div className="flex flex-col w-full h-fit gap-[24px]">
            {/* Tab bar */}
            <div className="flex w-full h-fit border-b border-[#EFF0F2] gap-[8px]">
              {tabButtons.map((tab) => (
                <Button
                  key={tab}
                  variant={
                    activeTab === tab
                      ? "tab_button_selected"
                      : "tab_button_default"
                  }
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </Button>
              ))}
            </div>

            {/* Active tabs */}
            <div className="w-full h-full">
              {activeTab === "Event Information" && eventInfo && (
                <EventInformation
                  eventInfo={eventInfo}
                  setEventInfo={setEventInfo}
                />
              )}
              {/* {activeTab === "Page Template" && (
                    <PageTemplate
                      handlePageClick={handlePageClick}
                      templateList={templateList}
                      templateListMeta={templateListMeta}
                      eventInfo={eventInfo}
                      setEventInfo={setEventInfo}
                    />
                  )} */}
              {activeTab === "Customizations" && templateCustomizations && (
                <Customizations
                  templateCustomizations={templateCustomizations}
                  setTemplateCustomizations={setTemplateCustomizations}
                  fontOptions={fontOptions}
                />
              )}
              {activeTab === "Monetary Contribution Settings" &&
                monetaryContributionSettings && (
                  <MonetaryContribution
                    monetaryContributionSettings={monetaryContributionSettings}
                    setMonetaryContributionSettings={
                      setMonetaryContributionSettings
                    }
                  />
                )}
            </div>
          </div>
        </div>
      </LoadingInComponent>
    </div>
  );
};

export default EventPage;
